/**
 * @isg/ui-kit — Theme Service
 *
 * Manages light/dark/system theme preference.
 * Sets `data-theme` attribute on <html> and watches for system changes.
 */

let _preference = 'light';
let _mediaQuery = null;
let _systemListener = null;

/**
 * Resolve the effective theme ('light' or 'dark') from a preference.
 */
function resolveTheme(preference) {
  if (preference === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return preference === 'dark' ? 'dark' : 'light';
}

/**
 * Apply a theme to the document.
 * @param {'light'|'dark'|'system'} preference
 */
export function applyTheme(preference = 'light') {
  _preference = preference;
  const resolved = resolveTheme(preference);
  document.documentElement.setAttribute('data-theme', resolved);

  // Add a brief transition class for smooth theme switching
  document.documentElement.classList.add('theme-transitioning');
  setTimeout(() => {
    document.documentElement.classList.remove('theme-transitioning');
  }, 350);

  // Watch or unwatch system preference changes
  _cleanupSystemListener();
  if (preference === 'system') {
    _watchSystemTheme();
  }

  // Persist to localStorage for immediate application on next page load
  try {
    localStorage.setItem('isg-theme', preference);
  } catch (e) {
    // localStorage may not be available
  }
}

/**
 * Get the current theme preference.
 */
export function getTheme() {
  return _preference;
}

/**
 * Get the resolved (effective) theme.
 */
export function getResolvedTheme() {
  return resolveTheme(_preference);
}

/**
 * Initialize theme from localStorage (call early, before render).
 * This prevents flash of wrong theme on page load.
 */
export function initTheme() {
  try {
    const saved = localStorage.getItem('isg-theme');
    if (saved) {
      applyTheme(saved);
      return;
    }
  } catch (e) {
    // ignore
  }
  applyTheme('light');
}

function _watchSystemTheme() {
  _mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  _systemListener = (e) => {
    if (_preference === 'system') {
      document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
    }
  };
  _mediaQuery.addEventListener('change', _systemListener);
}

function _cleanupSystemListener() {
  if (_mediaQuery && _systemListener) {
    _mediaQuery.removeEventListener('change', _systemListener);
    _mediaQuery = null;
    _systemListener = null;
  }
}
